/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {error, info, warn} from '@ion/core/src/logger/logger';
import {DLSThemeModes, DLSThemes} from './types';

const PREFIX = 'DLS Theme Manager';

/**
 * Logs a successful theme change event when the user or system switches
 * from one theme to another. This is emitted whenever the currentTheme
 * signal is updated via setCurrentTheme().
 *
 * @param oldTheme The previous theme identifier before the change.
 * @param newTheme The new theme identifier after the change.
 */
export const logThemeChange = (oldTheme: DLSThemes, newTheme: DLSThemes): void => {
  log(`Theme changed from ${oldTheme} to ${newTheme}`);
};

/**
 * Logs a successful theme mode change event when the user or system switches
 * between the light and dark modes. This is emitted whenever the themeMode
 * signal is updated via setThemeMode().
 *
 * @param oldMode The previous theme mode before the change.
 * @param newMode The new theme mode after the change.
 */
export const logThemeModeChange = (oldMode: DLSThemeModes, newMode: DLSThemeModes): void => {
  log(`Theme mode changed from ${oldMode} to ${newMode}`);
};

/**
 * Logs changes to the reload behavior configuration. This indicates whether
 * the application should automatically reload when a theme change occurs.
 * Useful for tracking configuration changes to the theme switching behavior.
 *
 * @param enabled `true` when reload on theme change is enabled, `false`
 *                when disabled.
 */
export const logReloadBehaviorChange = (enabled: boolean): void => {
  log(`Reload on theme change ${enabled ? 'enabled' : 'disabled'}`);
};

/**
 * Logs successful theme resolution. This is emitted after a theme has been
 * successfully loaded and created from its corresponding theme.json5 file.
 * Indicates that the theme creation process completed without errors.
 *
 * @param themeId The theme identifier that was successfully resolved.
 */
export const logThemeResolution = (themeId: DLSThemes): void => {
  log(`Theme resolved: ${themeId}`);
};

/**
 * Logs errors that occur during theme resolution. This is emitted when the
 * theme loading or creation process fails, including file loading errors or
 * theme creation exceptions. The error details are passed to the underlying
 * logger for debugging purposes.
 *
 * @param themeId The theme identifier that failed to resolve.
 * @param err     The error object or exception that occurred during resolution.
 */
export const logThemeResolutionError = (
  themeId: DLSThemes,
  err: unknown
): void => {
  log(`Failed to resolve theme for identifier "${themeId}"`, 'error');
};

/**
 * Logs the fallback to the legacy theme when theme resolution fails. This
 * warning indicates that the requested theme could not be loaded, so the
 * system is reverting to the stable legacy theme to ensure the application
 * remains functional.
 *
 * @param themeId The theme identifier that failed and triggered the fallback.
 */
export const logThemeResolutionFallback = (themeId: DLSThemes): void => {
  log(`Falling back to legacy theme from ${themeId}`, 'warn');
};

/**
 * Logs theme updates that occur when the currentTheme signal changes and
 * the `useThemeContext` hook effect re-runs. This is emitted after the new
 * theme has been resolved and applied to the application state.
 *
 * @param themeId The new theme identifier that was applied.
 */
export const logThemeUpdate = (themeId: DLSThemes): void => {
  log(`Theme updated to ${themeId}`);
};

/**
 * Logs the triggering of a page reload in response to a theme change. This
 * is emitted when shouldReloadOnThemeChange is enabled and a theme switch
 * occurs, indicating the application will refresh to apply the new theme.
 */
export const logPageReload = (): void => {
  log('Triggering page reload on theme change');
};

/**
 * Logs user interactions with the ThemeToggle component. This is emitted
 * when a user clicks the theme toggle button to switch between themes,
 * providing visibility into user-initiated theme changes.
 *
 * @param fromTheme The theme identifier before the user's toggle action.
 * @param toTheme   The theme identifier after the user's toggle action.
 */
export const logThemeToggleClick = (
  fromTheme: DLSThemes,
  toTheme: DLSThemes
): void => {
  log(`User toggled theme from ${fromTheme} to ${toTheme}`);
};

/**
 * Logs user interactions with the theme mode toggle. This is emitted when a
 * user clicks the toggle to switch between the light and dark modes,
 * providing visibility into user-initiated theme mode changes.
 *
 * @param fromMode The theme mode before the user's toggle action.
 * @param toMode   The theme mode after the user's toggle action.
 */
export const logThemeModeToggleClick = (
  fromMode: DLSThemeModes,
  toMode: DLSThemeModes
): void => {
  log(`User toggled theme mode from ${fromMode} to ${toMode}`);
};

/**
 * Logs imperative access to the current theme identifier via the
 * getCurrentTheme() function. This is useful for tracking when components
 * or utilities access the theme state outside of reactive contexts.
 *
 * @param themeId The current theme identifier that was accessed.
 */
export const logCurrentThemeAccess = (themeId: DLSThemes): void => {
  log(`Current theme accessed: ${themeId}`);
};

/**
 * Provides the logger to be invoked when fonts have been loaded for a theme.
 */
export const themeFontsLoaded = (themeName: string, fontCSS: string, success = true) => {
  if (success) {
    log(`Successfully loaded fonts for theme '${themeName}'. Contents: ${fontCSS}`, 'info');
  } else {
    log(`Failed to load fonts for theme '${themeName}'. Contents: ${fontCSS}`, 'error');
  }
};

/**
 * Provides the default logging facility through which all DLS Theme logs are
 * generated and emitted.
 *
 * @param message Defines the message to be logged to the underlying logger.
 * @param level   Defines the log level ('info', 'warn', or 'error').
 */
const log = (message: string, level: 'info' | 'warn' | 'error' = 'info'): void => {
  const loggers = {info, warn, error};
  loggers[level](`${PREFIX}: ${message}`);
};
